<template>
  <div ref="notificationContainer" class="global-notification-container">
    <transition-group
      name="notification"
      tag="div"
      class="notification-list"
      @enter="handleEnter"
      @leave="handleLeave"
    >
      <div
        v-for="notification in notifications"
        :key="notification.id"
        :class="[
          'notification-item',
          `notification-${notification.type}`,
          { 'notification-persistent': notification.persistent }
        ]"
        :aria-live="notification.type === 'error' ? 'assertive' : 'polite'"
        :aria-atomic="true"
        @click="handleNotificationClick(notification.id)"
        @mouseenter="pauseNotification(notification.id)"
        @mouseleave="resumeNotification(notification.id)"
        role="alert"
        tabindex="0"
        @keydown.enter="removeNotification(notification.id)"
        @keydown.escape="removeNotification(notification.id)"
      >
        <div class="notification-content">
          <el-icon :size="20" class="notification-icon">
            <component :is="getNotificationIcon(notification.type)" />
          </el-icon>
          <div class="notification-text">
            <h4 v-if="notification.title" class="notification-title">{{ notification.title }}</h4>
            <p class="notification-message">{{ notification.message }}</p>
          </div>
          <el-button
            type="text"
            class="close-button"
            @click.stop="removeNotification(notification.id)"
            aria-label="关闭通知"
          >
            <el-icon :size="16"><CloseBold /></el-icon>
          </el-button>
        </div>
        <div
          v-if="!notification.persistent"
          class="progress-bar"
          :style="{ width: getProgress(notification) + '%' }"
        ></div>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useAppStore } from '@/stores/app';
import type { Notification } from '@/stores/app';
import {
  Check,
  WarningFilled,
  InfoFilled,
  CloseBold,
  CircleCloseFilled
} from '@element-plus/icons-vue';

// 通知类型定义
interface NotificationItem extends Notification {
  paused?: boolean;
  pauseTime?: number;
  originalDuration?: number;
}

// 状态管理
const appStore = useAppStore();
const notifications = computed<NotificationItem[]>(() => appStore.notifications as NotificationItem[]);
const animationTimer = ref<number | null>(null);
const notificationContainer = ref<HTMLElement | null>(null);

// 获取通知图标
const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'success':
      return Check;
    case 'warning':
      return WarningFilled;
    case 'info':
      return InfoFilled;
    case 'error':
      return CircleCloseFilled;
    default:
      return InfoFilled;
  }
};

// 计算进度条宽度
const getProgress = (notification: NotificationItem): number => {
  if (notification.persistent || notification.paused) {
    return 100;
  }
  
  const elapsed = notification.pauseTime 
    ? Date.now() - notification.timestamp - notification.pauseTime 
    : Date.now() - notification.timestamp;
  
  const remaining = Math.max(0, notification.duration - elapsed);
  return (remaining / notification.duration) * 100;
};

// 移除通知
const removeNotification = (id: number) => {
  appStore.removeNotification(id);
};

// 处理通知点击
const handleNotificationClick = (id: number) => {
  const notification = notifications.value.find(n => n.id === id);
  if (notification?.onClick) {
    notification.onClick();
  }
  // 如果没有指定点击处理函数且不是持久化通知，则移除
  if (!notification?.onClick && !notification?.persistent) {
    removeNotification(id);
  }
};

// 暂停通知计时
const pauseNotification = (id: number) => {
  const notification = notifications.value.find(n => n.id === id);
  if (notification && !notification.persistent && !notification.paused) {
    notification.paused = true;
    notification.pauseStartTime = Date.now();
  }
};

// 恢复通知计时
const resumeNotification = (id: number) => {
  const notification = notifications.value.find(n => n.id === id);
  if (notification && !notification.persistent && notification.paused && notification.pauseStartTime) {
    notification.pauseTime = (notification.pauseTime || 0) + (Date.now() - notification.pauseStartTime);
    notification.paused = false;
    delete notification.pauseStartTime;
  }
};

// 处理进入动画
const handleEnter = (el: HTMLElement) => {
  // 可以在这里添加额外的进入动画逻辑
  el.classList.add('notification-entering');
  setTimeout(() => {
    el.classList.remove('notification-entering');
  }, 300);
};

// 处理离开动画
const handleLeave = (el: HTMLElement) => {
  // 可以在这里添加额外的离开动画逻辑
};

// 自动移除过期的通知
const checkAndRemoveExpiredNotifications = () => {
  const now = Date.now();
  notifications.value.forEach(notification => {
    if (
      !notification.persistent && 
      !notification.paused &&
      (notification.pauseTime 
        ? now - notification.timestamp - notification.pauseTime >= notification.duration
        : now - notification.timestamp >= notification.duration
      )
    ) {
      removeNotification(notification.id);
    }
  });
};

// 初始化
onMounted(() => {
  // 定时器用于更新进度条和检查过期通知
  animationTimer.value = window.setInterval(() => {
    checkAndRemoveExpiredNotifications();
  }, 100);
  
  // 监听窗口大小变化，调整通知位置
  window.addEventListener('resize', adjustNotificationPosition);
});

// 清理资源
onUnmounted(() => {
  if (animationTimer.value) {
    clearInterval(animationTimer.value);
  }
  window.removeEventListener('resize', adjustNotificationPosition);
});

// 调整通知位置
const adjustNotificationPosition = () => {
  // 在响应式设计中可能需要调整通知的位置
  // 这里可以根据窗口大小动态调整样式
};

// 导出方法供父组件调用（可选）
defineExpose({
  addNotification: appStore.addNotification.bind(appStore),
  removeNotification
});
</script>

<style scoped lang="scss">


.global-notification-container {
  position: fixed;
  top: $spacing-lg;
  right: $spacing-lg;
  z-index: $z-index-notification;
  display: flex;
  flex-direction: column;
  gap: $spacing-md;
  max-width: 380px;
  width: 100%;
  pointer-events: none;
  
  // 响应式调整
  @media (min-width: #{$breakpoint-md}) {
    top: $spacing-md;
    right: $spacing-md;
    max-width: calc(100vw - #{$spacing-md * 2});
  }
}

.notification-list {
  display: flex;
  flex-direction: column;
  gap: $spacing-md;
}

.notification-item {
  position: relative;
  background-color: $bg-color-card;
  border-radius: $border-radius-medium;
  box-shadow: $box-shadow-card;
  overflow: hidden;
  cursor: pointer;
  transition: all $transition-duration-base $transition-timing-base;
  border-left: 4px solid transparent;
  pointer-events: all;
  
  // 键盘焦点样式
  &:focus {
    outline: 2px solid $primary-color;
    outline-offset: 2px;
  }
  
  // 悬浮效果
  &:hover {
    transform: translateX(-4px);
    box-shadow: $box-shadow-dropdown;
  }
  
  // 进入动画期间的样式
  &.notification-entering {
    animation: notificationPulse 0.6s ease-in-out;
  }
}

// 不同类型的通知样式
.notification-success {
  border-left-color: $success-color;
  
  .notification-icon {
    color: $success-color;
  }
}

.notification-warning {
  border-left-color: $warning-color;
  
  .notification-icon {
    color: $warning-color;
  }
}

.notification-error {
  border-left-color: $danger-color;
  
  .notification-icon {
    color: $danger-color;
  }
}

.notification-info {
  border-left-color: $info-color;
  
  .notification-icon {
    color: $info-color;
  }
}

// 持久化通知样式
.notification-persistent {
  border-left-width: 6px;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 8px;
    height: 8px;
    background-color: $primary-color;
    border-radius: 50%;
    margin: 12px;
    animation: pulse 2s infinite;
  }
}

// 通知内容样式
.notification-content {
  display: flex;
  align-items: flex-start;
  padding: $spacing-md;
  gap: $spacing-md;
}

.notification-icon {
  flex-shrink: 0;
  margin-top: 2px; // 对齐文本
}

.notification-text {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: $font-size-base;
  font-weight: $font-weight-medium;
  color: $text-primary;
  margin: 0 0 $spacing-xs 0;
  line-height: 1.4;
}

.notification-message {
  font-size: $font-size-small;
  color: $text-regular;
  margin: 0;
  line-height: 1.5;
  word-break: break-word;
}

// 关闭按钮样式
.close-button {
  flex-shrink: 0;
  padding: 4px;
  color: $text-secondary;
  transition: color $transition-duration-fast;
  
  &:hover {
    color: $text-primary;
    background-color: $bg-color-hover;
  }
  
  &:focus {
    color: $text-primary;
  }
}

// 进度条样式
.progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background-color: transparent;
  transition: width $transition-duration-base linear;
  
  // 为不同类型的通知设置不同的进度条颜色
  .notification-success & {
    background: linear-gradient(to right, $success-color, color-mix(in srgb, $success-color, white 20%));
  }
  
  .notification-warning & {
    background: linear-gradient(to right, $warning-color, color-mix(in srgb, $warning-color, white 20%));
  }
  
  .notification-error & {
    background: linear-gradient(to right, $danger-color, color-mix(in srgb, $danger-color, white 20%));
  }
  
  .notification-info & {
    background: linear-gradient(to right, $info-color, color-mix(in srgb, $info-color, white 20%));
  }
}

// 动画定义
@keyframes notificationPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

// 过渡动画
.notification-enter-active,
.notification-leave-active {
  transition: all $transition-duration-base $transition-timing-base;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%) translateY(-10px);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%) translateY(10px);
}

.notification-move {
  transition: transform $transition-duration-base $transition-timing-base;
}

// 无障碍访问优化
@media (prefers-reduced-motion: reduce) {
  .notification-item,
  .notification-enter-active,
  .notification-leave-active,
  .notification-move,
  .progress-bar {
    transition: none;
  }
  
  .notification-entering {
    animation: none !important;
  }
  
  .notification-persistent::after {
    animation: none !important;
  }
  
  .notification-enter-from {
    transform: translateX(100%);
  }
  
  .notification-leave-to {
    transform: translateX(100%);
  }
}

.notification-content {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
}

.notification-icon {
  flex-shrink: 0;
}

.notification-text {
  flex: 1;
  min-width: 0;
}

.notification-title {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.notification-message {
  margin: 0;
  font-size: 14px;
  color: #606266;
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
}

.close-button {
  flex-shrink: 0;
  padding: 0;
  width: 24px;
  height: 24px;
  margin-left: 8px;
}

.progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background-color: currentColor;
  transition: width 0.1s linear;
}

.notification-success .progress-bar {
  background-color: #67c23a;
}

.notification-warning .progress-bar {
  background-color: #e6a23c;
}

.notification-info .progress-bar {
  background-color: #409eff;
}

.notification-error .progress-bar {
  background-color: #f56c6c;
}

.notification-success .notification-icon {
  color: #67c23a;
}

.notification-warning .notification-icon {
  color: #e6a23c;
}

.notification-info .notification-icon {
  color: #409eff;
}

.notification-error .notification-icon {
  color: #f56c6c;
}

/* 过渡动画 */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.notification-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.notification-leave-active {
  position: absolute;
  right: 0;
  width: calc(100% - 20px);
}

.notification-move {
  transition: transform 0.3s ease;
}
</style>